<template>
  <div v-if="needRefresh && !appStore.isAuthenticating" class="toast toast-start toast-top sm:toast-bottom z-50">
    <div class="alert alert-info shadow-lg gap-2">
      <span>A new version is available.</span>
      <div class="flex gap-2">
        <button class="btn btn-sm btn-ghost" @click="dismiss">Later</button>
        <button class="btn btn-sm btn-primary" @click="handleUpdate">Update now</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import { useRegisterSW } from 'virtual:pwa-register/vue';
import { FiveMinutesInMilliseconds } from '@/constants';
import { useApplicationStore } from '@/stores/Application';
import { createReloadGuard } from '@/utils/pwa-reload-guard';

// tmi-6gh T3: a reload in the middle of a sign-in / token handoff (while user + permissions are being
// fetched) can leave the user authenticated-but-freemium. Two paths could cause one, and both wait:
//  - this tab's "Update now": the banner is hidden while `isAuthenticating`, so it cannot be clicked;
//  - a `controllerchange` caused by ANOTHER tab's "Update now" (tmi-udo reloads every client): the reload
//    guard defers it until `isAuthenticating` turns false, then reloads once (F-I3).
const appStore = useApplicationStore();

const { needRefresh, updateServiceWorker } = useRegisterSW( {
    immediate: true,
    onRegisteredSW( _swUrl, registration ) {
        if ( !registration ) return;

        setInterval( async () => {
            if ( !( !registration.installing && navigator ) ) return;
            if ( 'connection' in navigator && !navigator.onLine ) return;

            await registration.update();
        }, FiveMinutesInMilliseconds );
    },
} );

const reloadGuard = createReloadGuard( {
    hadController: 'serviceWorker' in navigator && null !== navigator.serviceWorker.controller,
    reload: () => window.location.reload(),
    isBusy: () => appStore.isAuthenticating,
    // One-shot: stops itself the first time the sign-in settles, then performs the deferred reload.
    onIdle: resume => {
        const stop = watch( () => appStore.isAuthenticating, authenticating => {
            if ( authenticating ) return;
            stop();
            resume();
        } );
    }
} );
if ( 'serviceWorker' in navigator ) {
    navigator.serviceWorker.addEventListener( 'controllerchange', () => reloadGuard.onControllerChange() );
}

function dismiss() {
    needRefresh.value = false;
}

async function handleUpdate() {
    reloadGuard.userRequestedUpdate();
    await updateServiceWorker( true );
}
</script>
